import { Award, Medal, Trophy, Crown, type LucideIcon } from "lucide-react";

export type BadgeTier = "bronze" | "silver" | "gold" | "platinum";

export type BadgeTierDef = {
  key: BadgeTier;
  label: string;
  /** Lifetime impact in *cents* required to earn this tier (per pillar). */
  thresholdCents: number;
  icon: LucideIcon;
  /** Tailwind class (semantic token) for the medal color. */
  colorClass: string;
  /** Used for shadows / glows. */
  glowClass: string;
  ringClass: string;
};

export const BADGE_TIERS: BadgeTierDef[] = [
  {
    key: "bronze",
    label: "Bronze",
    thresholdCents: 2500,
    icon: Award,
    colorClass: "text-amber-600",
    glowClass: "shadow-[0_0_24px_-4px_hsl(28_85%_55%/0.6)]",
    ringClass: "ring-amber-600/40",
  },
  {
    key: "silver",
    label: "Silver",
    thresholdCents: 10000,
    icon: Medal,
    colorClass: "text-slate-300",
    glowClass: "shadow-[0_0_24px_-4px_hsl(210_15%_80%/0.6)]",
    ringClass: "ring-slate-300/40",
  },
  {
    key: "gold",
    label: "Gold",
    thresholdCents: 50000,
    icon: Trophy,
    colorClass: "text-gold",
    glowClass: "shadow-gold",
    ringClass: "ring-gold/40",
  },
  {
    key: "platinum",
    label: "Platinum",
    thresholdCents: 200000,
    icon: Crown,
    colorClass: "text-cyan-300",
    glowClass: "shadow-[0_0_28px_-4px_hsl(190_90%_70%/0.7)]",
    ringClass: "ring-cyan-300/40",
  },
];

export const tierByKey = (key?: BadgeTier | string | null) =>
  BADGE_TIERS.find((t) => t.key === key);

/** Returns the highest tier earned for a given lifetime cents amount. */
export const tierForCents = (cents: number): BadgeTierDef | null => {
  let earned: BadgeTierDef | null = null;
  for (const tier of BADGE_TIERS) {
    if (cents >= tier.thresholdCents) earned = tier;
  }
  return earned;
};

/** Returns the next tier above the given cents, or null if maxed out. */
export const nextTierForCents = (cents: number): BadgeTierDef | null => {
  return BADGE_TIERS.find((t) => cents < t.thresholdCents) ?? null;
};
